// Authentication API service

import { API_BASE_URL } from '@/config/env'
import type {
  LoginRequest,
  LoginResponse,
  RefreshTokenRequest,
  RefreshTokenResponse,
  OAuthInitiateRequest,
  OAuthInitiateResponse,
  OAuthCallbackRequest,
  OAuthCallbackResponse,
  LogoutResponse,
  User,
  ApiError,
  UpdateProfileRequest,
  UpdatePasswordRequest,
  UpdateProfileResponse,
  AuthResponse,
  SseTokenRequest,
  SseTokenResponse,
} from './types';
import { useAuthStore } from '@/stores/auth-store';

// API configuration
const AUTH_API_BASE = `${API_BASE_URL}/api/v1/auth`;
const USER_API_BASE = `${API_BASE_URL}/api/core/user`;

// Helper function to decode JWT and extract user info
function decodeJWT(token: string): any {
  try {
    const base64Url = token.split('.')[1];
    const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
    const jsonPayload = decodeURIComponent(
      atob(base64)
        .split('')
        .map(c => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
        .join('')
    );
    return JSON.parse(jsonPayload);
  } catch (error) {
    console.error('Failed to decode JWT:', error);
    return {};
  }
}

/**
 * Resolve a user's roles from the access token, falling back to any roles the backend
 * supplied and finally to the least-privileged default.
 *
 * Roles live in the JWT's `realm_access.roles`. Reading them anywhere else is how the
 * OAuth path silently produced non-admins regardless of Keycloak.
 */
export function rolesFromToken(accessToken?: string, responseRoles?: string[]): string[] {
  if (accessToken) {
    const payload = decodeJWT(accessToken);
    const tokenRoles = payload?.realm_access?.roles;
    if (Array.isArray(tokenRoles) && tokenRoles.length > 0) {
      return tokenRoles;
    }
  }
  if (Array.isArray(responseRoles) && responseRoles.length > 0) {
    return responseRoles;
  }
  return ['user'];
}

// Request helper with error handling
async function fetchWithErrorHandling<T>(
  url: string,
  options: RequestInit = {}
): Promise<T> {
  try {
    const response = await fetch(url, {
      ...options,
      headers: {
        ...options.headers,
        'Content-Type': 'application/json',
      },
    });

    if (!response.ok) {
      const errorData = await response.json().catch(() => ({
        message: `HTTP error! status: ${response.status}`,
      }));

      // Handle USER_NOT_FOUND_NEEDS_ONBOARDING error - redirect to onboarding
      const errorCode = errorData.errorCode || errorData.code;
      if (errorCode === 'USER_NOT_FOUND_NEEDS_ONBOARDING') {
        // Use window.location for hard redirect to ensure clean state
        window.location.href = '/onboarding';
        // Still throw the error for proper error chain handling
      }

      throw {
        message: errorData.message || errorData.detail || 'An error occurred',
        status: response.status,
        code: errorCode,
      } as ApiError;
    }

    return await response.json();
  } catch (error) {
    if ((error as ApiError).message) {
      throw error;
    }

    if (error instanceof TypeError && error.message === 'Failed to fetch') {
      throw {
        message: 'Unable to connect to the server. Please check your connection.',
        status: 0,
      } as ApiError;
    }

    throw {
      message: 'An unexpected error occurred',
      status: 0,
    } as ApiError;
  }
}

// Authentication API methods
export const authApi = {
  // Login with username and password
  async login(credentials: LoginRequest): Promise<LoginResponse> {
    const response = await fetchWithErrorHandling<any>(`${AUTH_API_BASE}/login`, {
      method: 'POST',
      body: JSON.stringify(credentials),
    });

    // Decode JWT to extract user information (as fallback for basic claims)
    const jwtPayload = decodeJWT(response.accessToken);

    // Map backend response fields to frontend expectations
    // Backend now provides full user profile data including registration status (same as OAuth flow)
    return {
      success: response.success || true,
      access_token: response.accessToken,
      refresh_token: response.refreshToken,
      user: {
        id: response.userId || jwtPayload.sub || '',
        email: response.email || jwtPayload.email || credentials.username,
        name: `${response.firstName || jwtPayload.given_name || ''} ${response.lastName || jwtPayload.family_name || ''}`.trim() || jwtPayload.preferred_username || credentials.username,
        firstName: response.firstName || jwtPayload.given_name,
        lastName: response.lastName || jwtPayload.family_name,
        // rolesFromToken decodes the JWT claim with the response body as fallback and
        // never hardcodes ['user'] — a missing claim must not silently demote admins.
        roles: rolesFromToken(response.accessToken, response.roles),
        // Organization and tenant info from PostgreSQL lookup
        organizationId: response.organizationId,
        tenantId: response.tenantId,
        // Subscription plan from the login response (Phase 1) so plan-gated UI
        // resolves synchronously — no separate org-sync round-trip needed.
        subscriptionPlan: response.subscriptionPlan,
        // Tenant status (Task 9) — 'active' | 'suspended' | 'deleted'. Read by
        // LoginPage to route to AccountSuspendedPage instead of the normal
        // post-login landing.
        tenantStatus: response.tenantStatus,
        // Registration and onboarding status (same as OAuth callback)
        registrationStatus: response.registrationStatus,
        onboardingProgress: response.onboardingProgress,
        onboardingStatus: response.onboardingStatus,
        created_at: response.timestamp || new Date().toISOString(),
      },
    };
  },

  // Refresh access token
  async refreshToken(request: RefreshTokenRequest): Promise<RefreshTokenResponse> {
    const response = await fetchWithErrorHandling<any>(`${AUTH_API_BASE}/refresh`, {
      method: 'POST',
      body: JSON.stringify({
        refreshToken: request.refresh_token,  // Backend expects camelCase: RefreshTokenRequest.refreshToken
      }),
    });

    // Map backend camelCase fields to frontend snake_case expectations
    return {
      success: response.success || true,
      access_token: response.accessToken,
      refresh_token: response.refreshToken || request.refresh_token, // Keep existing if not returned
    };
  },

  // Initiate OAuth login
  async initiateOAuth(request: OAuthInitiateRequest): Promise<OAuthInitiateResponse> {
    return fetchWithErrorHandling<OAuthInitiateResponse>(`${AUTH_API_BASE}/oauth/init`, {
      method: 'POST',
      body: JSON.stringify(request),
    });
  },

  // Handle OAuth callback
  async handleOAuthCallback(request: OAuthCallbackRequest): Promise<OAuthCallbackResponse> {
    const response = await fetchWithErrorHandling<any>(`${AUTH_API_BASE}/oauth/callback`, {
      method: 'POST',
      body: JSON.stringify(request),
    });

    // Backend now provides all user info (from JWT + PostgreSQL check)
    // Map backend camelCase fields to frontend expectations
    return {
      success: response.success || true,
      access_token: response.accessToken,
      refresh_token: response.refreshToken,
      user: {
        id: response.userId || '',
        email: response.email || '',
        name: `${response.firstName || ''} ${response.lastName || ''}`.trim() || response.username || '',
        firstName: response.firstName,
        lastName: response.lastName,
        roles: rolesFromToken(response.accessToken, response.roles),
        organizationId: response.organizationId,
        tenantId: response.tenantId,
        // Subscription plan from the OAuth callback response (Phase 1).
        subscriptionPlan: response.subscriptionPlan,
        // Tenant status (Task 9) — same as the password login path; a
        // one-path-only omission here would let a suspended tenant's OAuth
        // users bypass the AccountSuspendedPage redirect.
        tenantStatus: response.tenantStatus,
        // Registration and onboarding status
        registrationStatus: response.registrationStatus,
        onboardingProgress: response.onboardingProgress,
        onboardingStatus: response.onboardingStatus,
        created_at: response.timestamp || new Date().toISOString(),
      },
    };
  },

  // Logout user
  async logout(accessToken: string): Promise<LogoutResponse> {
    return fetchWithErrorHandling<LogoutResponse>(`${AUTH_API_BASE}/logout`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });
  },

  // Get user profile
  async getUserProfile(accessToken: string): Promise<User> {
    const response = await fetchWithErrorHandling<any>(`${USER_API_BASE}/profile`, {
      method: 'GET',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
    });

    // Map backend response to frontend User type
    return {
      id: response.userId || response.id || '',
      email: response.email || '',
      name: response.displayName || `${response.firstName || ''} ${response.lastName || ''}`.trim(),
      firstName: response.firstName,
      lastName: response.lastName,
      organization: response.organizationName || response.company,
      organizationId: response.organizationId,
      organizationName: response.organizationName,
      tenantId: response.tenantId,
      created_at: response.createdAt || response.created_at,
    } as User;
  },

  // Send OTP for email verification (alpha phase)
  async sendOtp(email: string): Promise<{
    success: boolean;
    message: string;
    email: string;
    ttlSeconds: number;
  }> {
    return fetchWithErrorHandling<any>(`${AUTH_API_BASE}/send-otp`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Verify OTP and get verification token
  async verifyOtp(email: string, otp: string, purpose?: string): Promise<{
    success: boolean;
    message: string;
    verificationToken?: string;
    ttlSeconds?: number;
  }> {
    return fetchWithErrorHandling<any>(`${AUTH_API_BASE}/verify-otp`, {
      method: 'POST',
      body: JSON.stringify({ email, otp, ...(purpose && { purpose }) }),
    });
  },

  // Register new user
  async register(request: {
    email: string;
    password: string;
    firstName: string;
    lastName: string;
    verificationToken?: string;
  }): Promise<AuthResponse> {
    try {
      const response = await fetchWithErrorHandling<any>(`${AUTH_API_BASE}/register`, {
        method: 'POST',
        body: JSON.stringify(request),
      });

      // Backend now returns camelCase consistently
      return {
        success: response.success !== false,
        accessToken: response.accessToken,
        refreshToken: response.refreshToken,
        tokenType: response.tokenType || 'Bearer',
        expiresIn: response.expiresIn || 3600,
        refreshExpiresIn: response.refreshExpiresIn,
        scope: response.scope,
        userId: response.userId || '',
        username: response.username,
        email: response.email || request.email,
        firstName: response.firstName || request.firstName,
        lastName: response.lastName || request.lastName,
        tenantId: response.tenantId,
        organizationId: response.organizationId,
        registrationStatus: response.registrationStatus,
        onboardingProgress: response.onboardingProgress,
        onboardingStatus: response.onboardingStatus,
        error: response.error,
        errorDescription: response.errorDescription,
      };
    } catch (error: any) {
      throw new Error(error.response?.data?.message || error.message || 'Registration failed');
    }
  },

  // Update user profile
  async updateProfile(
    data: UpdateProfileRequest,
    accessToken: string
  ): Promise<UpdateProfileResponse> {
    const response = await fetchWithErrorHandling<any>(`${USER_API_BASE}/profile`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    return {
      success: response.success || true,
      user: {
        id: response.id || response.userId || '',
        email: response.email || data.email || '',
        name: response.name || `${data.firstName || ''} ${data.lastName || ''}`.trim(),
        firstName: response.firstName || data.firstName,
        lastName: response.lastName || data.lastName,
        created_at: response.createdAt || response.created_at,
      },
      message: response.message || 'Profile updated successfully',
    };
  },

  // Update password
  async updatePassword(
    data: UpdatePasswordRequest,
    accessToken: string
  ): Promise<{ success: boolean; message: string }> {
    const response = await fetchWithErrorHandling<any>(`${USER_API_BASE}/password`, {
      method: 'PUT',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify(data),
    });

    return {
      success: response.success || true,
      message: response.message || 'Password updated successfully',
    };
  },

  // Request password reset OTP
  async forgotPassword(email: string): Promise<{ success: boolean; message: string; ttlSeconds?: number }> {
    return fetchWithErrorHandling<any>(`${AUTH_API_BASE}/forgot-password`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });
  },

  // Reset password with verification token (or OTP as fallback)
  async resetPassword(
    email: string,
    verificationToken: string,
    newPassword: string,
    confirmPassword: string
  ): Promise<{ success: boolean; message: string }> {
    return fetchWithErrorHandling<any>(`${AUTH_API_BASE}/reset-password`, {
      method: 'POST',
      body: JSON.stringify({ email, verificationToken, newPassword, confirmPassword }),
    });
  },

  // Change password (requires authentication)
  async changePassword(
    data: UpdatePasswordRequest,
    accessToken: string
  ): Promise<{ success: boolean; message: string }> {
    return fetchWithErrorHandling<any>(`${AUTH_API_BASE}/change-password`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${accessToken}`,
      },
      body: JSON.stringify({
        currentPassword: data.currentPassword,
        newPassword: data.newPassword,
        confirmPassword: data.newPassword,
      }),
    });
  },

  // Check email domain for tenant discovery
  async checkEmailDomain(email: string): Promise<{
    tenantFound: boolean;
    tenantId?: string;
    tenantName?: string;
    suggestedTenantName?: string;
    domain?: string;
    message?: string;
  }> {
    const response = await fetchWithErrorHandling<any>(`${API_BASE_URL}/api/core/mt/tenants/discover`, {
      method: 'POST',
      body: JSON.stringify({ email }),
    });

    return {
      tenantFound: response.tenantFound || false,
      tenantId: response.tenantId,
      tenantName: response.tenantName,
      suggestedTenantName: response.suggestedTenantName,
      domain: response.domain,
      message: response.message,
    };
  },

  // Complete OAuth registration with organization details
  async completeOAuthRegistration(
    tenantName: string,
    organizationName: string
  ): Promise<AuthResponse> {
    // Get the current access token from auth store
    const tokens = useAuthStore.getState().tokens;

    if (!tokens || !tokens.accessToken) {
      throw {
        message: 'No authentication token found. Please log in again.',
        status: 401,
      } as ApiError;
    }

    const response = await fetchWithErrorHandling<any>(`${AUTH_API_BASE}/oauth/complete-registration`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokens.accessToken}`,
      },
      body: JSON.stringify({ tenantName, organizationName }),
    });

    return {
      success: response.success !== false,
      accessToken: response.accessToken || tokens.accessToken,
      refreshToken: response.refreshToken || tokens.refreshToken,
      tokenType: response.tokenType || 'Bearer',
      expiresIn: response.expiresIn || 3600,
      refreshExpiresIn: response.refreshExpiresIn,
      scope: response.scope,
      userId: response.userId || '',
      username: response.username,
      email: response.email || '',
      firstName: response.firstName,
      lastName: response.lastName,
      tenantId: response.tenantId,
      organizationId: response.organizationId,
      registrationStatus: response.registrationStatus,
      onboardingProgress: response.onboardingProgress,
      onboardingStatus: response.onboardingStatus,
      error: response.error,
      errorDescription: response.errorDescription,
    };
  },

  // Request SSE token for secure Server-Sent Events connection
  // SSE tokens are short-lived, single-use tokens for EventSource authentication
  async requestSseToken(request: SseTokenRequest): Promise<SseTokenResponse> {
    const tokens = useAuthStore.getState().tokens;

    if (!tokens || !tokens.accessToken) {
      throw {
        message: 'No authentication token found. Please log in to generate courses.',
        status: 401,
      } as ApiError;
    }

    const response = await fetchWithErrorHandling<any>(`${AUTH_API_BASE}/sse-token`, {
      method: 'POST',
      headers: {
        Authorization: `Bearer ${tokens.accessToken}`,
      },
      body: JSON.stringify(request),
    });

    return {
      token: response.token,
      expiresAt: response.expiresAt,
      ttlSeconds: response.ttlSeconds,
      purpose: response.purpose,
      success: response.success !== false,
    };
  },
};

// Export default for convenience
export default authApi;
